/**
 * Trip CO₂ Calculator — comparison-set builder.
 *
 * Two pure functions that turn the user's `(mode, config)` into the
 * chart's row set:
 *
 * 1. `buildSegmentList` — what to send to the batch endpoint
 *    (4 segments minimum, 5 when the user picks van/motorcycle/ferry).
 *    PRD §5.3.
 * 2. `buildComparison` — given the parallel `EmissionsResult[]`, returns
 *    the final chart-ready `ChartRow[]`, sorted ascending by kg, with
 *    the synthetic Plane+Train mix row added when round-trip is on.
 *    PRD §5.5.
 *
 * The synthetic mix row is computed *before* round-trip doubling — its
 * kg is the sum of one one-way plane leg and one one-way train leg
 * (which together cover the RT). Doubling the plane and train rows
 * separately is correct for those bars; the mix is its own thing.
 *
 * Phase 3 of the Trip CO₂ Calculator (see PRD §11).
 */

import type { EmissionsResult, TransportMode } from '../admin/carbonAccounting/api';
import type { ConfigByMode } from './modes';
import { DEFAULT_CONFIG, MODE_DISPLAY, getDefaultConfig, isCustom } from './modes';
import type { EmissionBreakdown, ResultRow } from './calculate';
import { toResultRow, withRoundTrip } from './calculate';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A typed (mode, config) pair — the unit the calculator sends to the
 * batch endpoint. The api layer (Phase 4/7) attaches origin/destination
 * before posting.
 *
 * Discriminated by mode so consumers can narrow `config` from `mode`.
 */
export type Segment = {
  [M in TransportMode]: { mode: M; config: ConfigByMode[M] };
}[TransportMode];

/**
 * Final chart-ready row. Distinct from `ResultRow` because the
 * Plane+Train mix row is synthetic — it isn't tied to a single mode.
 *
 * Labels are pre-computed (`label`, `primaryMode`) so the chart
 * component stays a dumb renderer.
 */
export interface ChartRow {
  /** Stable React key. `'mix'` for the synthetic row, otherwise the mode. */
  id: TransportMode | 'mix';
  /** `'mode'` for a real segment row; `'mix'` for the synthetic plane+train RT row. */
  kind: 'mode' | 'mix';
  /** Drives the chart icon / single-bar color (mix rows use airplane). */
  primaryMode: TransportMode;
  /** Display string, e.g. `"🚄 Train"`, `"🚗 Car (3 pax · electric)"`, `"✈️+🚄 One leg each"`. */
  label: string;
  kg: number;
  breakdown: EmissionBreakdown;
  distanceKm: number;
  isChosen: boolean;
  roundTrip: boolean;
  /**
   * Prepared, human-readable explanation of this row's calculation —
   * straight from the backend engine (`comprehensive.explanation`).
   * The chart's methodology panel renders these verbatim, one per row.
   *
   * For the synthetic mix row, this is composed from the plane and
   * train one-way explanations.
   */
  explanation?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Segment list
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The four modes that always appear on the chart (PRD §5.5 "Defaults" row).
 * Bus and Plane have no editable sub-config (locked); train and car can be
 * customised, in which case the default row is **replaced** with the user's.
 */
const DEFAULT_SET: ReadonlyArray<TransportMode> = ['train', 'bus', 'car', 'airplane'];

/**
 * Returns the one-way segments to send to the batch endpoint. PRD §5.3:
 *
 *   • Train, Bus, Car, Plane — always (4 segments).
 *   • If the user picked one of those four, that segment carries the
 *     user's config (which may equal the default — same wire shape
 *     either way).
 *   • If the user picked van / motorcycle / ferry, append a 5th segment
 *     with the user's config.
 *
 * The Plane+Train mix row is derived client-side; it is **not** in the
 * segment list.
 */
export function buildSegmentList<M extends TransportMode>(input: {
  mode: M;
  config: ConfigByMode[M];
}): Segment[] {
  const userSegment = { mode: input.mode, config: input.config } as Segment;

  const segments: Segment[] = DEFAULT_SET.map((m) =>
    m === input.mode
      ? userSegment
      : ({ mode: m, config: getDefaultConfig(m) } as Segment)
  );

  if (!DEFAULT_SET.includes(input.mode)) {
    segments.push(userSegment);
  }

  return segments;
}

// ─────────────────────────────────────────────────────────────────────────────
// Label helpers (private)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the row label, including the parenthetical config suffix.
 *
 * Two label policies:
 *   • **Vehicle modes** (car / van / motorcycle): always show the
 *     occupancy count, even when it equals the default. The number
 *     of people materially changes the per-passenger kg, so the
 *     comparison only reads fairly if it's visible at a glance.
 *   • **Passenger modes** (train / bus / plane / ferry): show a
 *     suffix only when the config differs from the default. Plane
 *     and bus have no editable sub-fields, so this only triggers
 *     for a diesel train.
 */
function makeModeLabel<M extends TransportMode>(
  mode: M,
  config: ConfigByMode[M]
): string {
  const { emoji, label } = MODE_DISPLAY[mode];

  if (mode === 'car') {
    const c = config as ConfigByMode['car'];
    const peopleWord = c.occupancy === 1 ? 'passenger' : 'passengers';
    const fuelSuffix =
      c.fuel === (DEFAULT_CONFIG.car as ConfigByMode['car']).fuel ? '' : `, ${c.fuel}`;
    return `${emoji} ${label} (with ${c.occupancy} ${peopleWord}${fuelSuffix})`;
  }

  if (mode === 'van') {
    const c = config as ConfigByMode['van'];
    const peopleWord = c.occupancy === 1 ? 'passenger' : 'passengers';
    return `${emoji} ${label} (with ${c.occupancy} ${peopleWord})`;
  }

  if (mode === 'motorcycle') {
    const c = config as ConfigByMode['motorcycle'];
    return `${emoji} ${label} (${c.occupancy === 1 ? '1 rider' : '2 riders'})`;
  }

  if (!isCustom(mode, config)) {
    return `${emoji} ${label}`;
  }

  // Passenger modes with non-default sub-config (today: diesel train).
  if (mode === 'train') {
    return `${emoji} ${label} (${(config as ConfigByMode['train']).type})`;
  }
  // Bus / plane / ferry are fully locked, so this is unreachable in v1
  // — fall back to a generic key=value rendering rather than throwing.
  return `${emoji} ${label} (${Object.values(config as Record<string, unknown>).join(' · ')})`;
}

// Mix row label — train emoji first to reflect the calculator's
// rail-favourable framing (PRD §3). Spaces around the `+` for
// legibility on small viewports.
const MIX_LABEL = `${MODE_DISPLAY.train.emoji} + ${MODE_DISPLAY.airplane.emoji} One leg each`;

// ─────────────────────────────────────────────────────────────────────────────
// Comparison
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the chart's full row set from the parallel `EmissionsResult[]`
 * returned by the batch endpoint.
 *
 * Contract: `results.length === buildSegmentList({ mode, config }).length`
 * (i.e. 4 or 5, in the same order). This is enforced — a mismatch
 * almost certainly means a wiring bug, and silent partial output
 * would corrupt the chart.
 *
 * Order of operations:
 *   1. Normalise each result into a one-way `ResultRow`, marking
 *      `isChosen` on the row whose mode matches the user's.
 *   2. Build the synthetic Plane+Train mix row from the *one-way*
 *      plane and train rows (only when round-trip is on).
 *   3. Apply round-trip doubling to every mode row (the mix row is
 *      already a sum of two legs, so its kg is the round-trip total).
 *   4. Sort ascending by kg (PRD §5.5).
 */
export function buildComparison<M extends TransportMode>(input: {
  userMode: M;
  userConfig: ConfigByMode[M];
  roundTrip: boolean;
  /** Parallel to `buildSegmentList({ mode: userMode, config: userConfig })`. */
  results: EmissionsResult[];
}): ChartRow[] {
  const segments = buildSegmentList({ mode: input.userMode, config: input.userConfig });

  if (segments.length !== input.results.length) {
    throw new Error(
      `buildComparison: expected ${segments.length} results for mode "${input.userMode}", got ${input.results.length}`
    );
  }

  // 1. One-way ResultRows, with isChosen marked on the user's segment.
  const oneWayRows: ResultRow[] = segments.map((seg, i) => {
    // The cast widens the per-mode union to the generic toResultRow
    // signature — runtime shape is fine because Segment carries the
    // matching (mode, config) pair.
    return toResultRow({
      mode: seg.mode,
      config: seg.config,
      result: input.results[i],
      isChosen: seg.mode === input.userMode,
    } as Parameters<typeof toResultRow>[0]);
  });

  // 2. Apply round-trip doubling to mode rows.
  const modeRows = input.roundTrip ? oneWayRows.map(withRoundTrip) : oneWayRows;
  const chartRows: ChartRow[] = modeRows.map((r, i) =>
    toChartRow(r, segments[i])
  );

  // 3. Add the synthetic mix row on round-trip (built from one-way values).
  if (input.roundTrip) {
    const planeOneWay = oneWayRows.find((r) => r.mode === 'airplane');
    const trainOneWay = oneWayRows.find((r) => r.mode === 'train');
    if (planeOneWay && trainOneWay) {
      chartRows.push(makeMixRow(planeOneWay, trainOneWay));
    }
  }

  // 4. Sort ascending by kg (PRD §5.5).
  chartRows.sort((a, b) => a.kg - b.kg);

  return chartRows;
}

// ─────────────────────────────────────────────────────────────────────────────
// Row construction (private)
// ─────────────────────────────────────────────────────────────────────────────

function toChartRow(row: ResultRow, segment: Segment): ChartRow {
  return {
    id: row.mode,
    kind: 'mode',
    primaryMode: row.mode,
    label: makeModeLabel(segment.mode, segment.config),
    kg: row.kg,
    breakdown: row.breakdown,
    distanceKm: row.distanceKm,
    isChosen: row.isChosen,
    roundTrip: row.roundTrip,
    explanation: row.explanation,
  };
}

/**
 * Builds the Plane+Train mix row from one-way plane and train rows.
 *
 * Math (PRD §5.3):
 *   mix.kg          = plane_oneway.kg + train_oneway.kg
 *   mix.distanceKm  = plane_oneway.distance + train_oneway.distance
 *   mix.breakdown   = component-wise sum
 *
 * The mix row only exists on round-trip, and represents the trip done
 * as one plane leg + one train leg (i.e. the full round-trip total).
 * `isChosen` is always false — the mix isn't a mode the user can
 * select, only a comparison framing.
 */
function makeMixRow(
  planeOneWay: ResultRow,
  trainOneWay: ResultRow
): ChartRow {
  return {
    id: 'mix',
    kind: 'mix',
    primaryMode: 'airplane', // dominant by impact — drives icon/color fallback
    label: MIX_LABEL,
    kg: planeOneWay.kg + trainOneWay.kg,
    breakdown: {
      electric: planeOneWay.breakdown.electric + trainOneWay.breakdown.electric,
      fuel: planeOneWay.breakdown.fuel + trainOneWay.breakdown.fuel,
      rfi: planeOneWay.breakdown.rfi + trainOneWay.breakdown.rfi,
    },
    distanceKm: planeOneWay.distanceKm + trainOneWay.distanceKm,
    isChosen: false,
    roundTrip: true,
    explanation: makeMixExplanation(planeOneWay.explanation, trainOneWay.explanation),
  };
}

/**
 * Builds the mix row's methodology blurb by composing the plane and
 * train one-way explanations under a short header. Returns undefined
 * if either is missing — the methodology panel will simply omit the
 * row instead of rendering a half-true partial explanation.
 */
function makeMixExplanation(
  planeExplanation?: string,
  trainExplanation?: string
): string | undefined {
  if (!planeExplanation || !trainExplanation) return undefined;
  return (
    `Sum of one plane leg and one train leg (one-way each), which together cover the round trip. ` +
    `Plane leg: ${planeExplanation} Train leg: ${trainExplanation}`
  );
}
